// Multi-page support for the unified renderer. Pages are derived from the
// layout: a section marked `pageBreak` starts a new page, everything before
// the first break is page 1. A page whose sections currently render nothing
// (all fields hidden by rules/flags) is skipped in navigation — which is how
// existing show/hide rules double as page-skip logic with no extra machinery.
// A `goto` action (P4) is the authored version of the same thing: a firing
// rule names the page Next lands on, and the pages between are jumped over.
import type { FieldDefinition, FormConfig, FormSection } from '../shared/types';
import { PublishedDef, widgetForSection } from './model';
import { FormEval, renderableIn } from './evaluate';
import { ruleFires } from './rules';

export interface FormPage {
  /** the id of the section that starts the page — what a `goto` names */
  id: string;
  title: string;
  sections: FormSection[];
}

/** Split the (sorted) sections into pages. A single page means "not paged". */
export function derivePages(sections: FormSection[]): FormPage[] {
  const sorted = sections.slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const pages: FormPage[] = [];
  for (const s of sorted) {
    if (!pages.length || s.pageBreak) pages.push({ id: s.id, title: s.pageTitle || '', sections: [] });
    else if (s.pageTitle && !pages[pages.length - 1].title) pages[pages.length - 1].title = s.pageTitle;
    pages[pages.length - 1].sections.push(s);
  }
  return pages;
}

/** The index of the page a section id starts, or -1 when it starts none. */
export const pageIndexOf = (pages: FormPage[], sectionId: string): number =>
  pages.findIndex((p) => p.id === sectionId);

/** Would this section render anything right now? Mirrors the renderer's own checks. */
export function sectionHasContent(section: FormSection, def: PublishedDef, ev: FormEval, cfg: FormConfig): boolean {
  if (section.showFlag && !cfg[section.showFlag]) return false;
  if (section.hideWith && cfg[section.hideWith]) return false;
  if (ev.hiddenSections.has(section.id)) return false;
  if (section.type === 'widget' || section.type === 'locations') {
    return !!widgetForSection(def.widgets, section)?.tree;
  }
  return def.fields.some((field) =>
    field.scope === 'order' && field.section === section.id && renderableIn(field, ev.order, cfg, ev));
}

export const pageHasContent = (page: FormPage, def: PublishedDef, ev: FormEval, cfg: FormConfig): boolean =>
  page.sections.some((s) => sectionHasContent(s, def, ev, cfg));

/** Field keys answered on this page — order-scope fields plus every scope of its widgets. */
export function pageFieldKeys(page: FormPage, def: PublishedDef): Set<string> {
  const keys = new Set<string>();
  const sectionIds = new Set(page.sections.filter((s) => s.type === 'fields' || s.type === 'totals' || s.type === 'files').map((s) => s.id));
  const widgetScopes = new Set<string>();
  for (const s of page.sections) {
    if (s.type !== 'widget' && s.type !== 'locations') continue;
    const widget = widgetForSection(def.widgets, s);
    const walk = (n: { scope: string; children: { scope: string; children: any[] }[] }) => {
      widgetScopes.add(n.scope);
      (n.children || []).forEach(walk);
    };
    if (widget?.tree) walk(widget.tree);
  }
  for (const field of def.fields as FieldDefinition[]) {
    if (field.scope === 'order' ? sectionIds.has(field.section) : widgetScopes.has(field.scope)) keys.add(field.key);
  }
  return keys;
}

export interface PageWalk {
  /** the pages (indices) the respondent walks, first to last, given the answers so far */
  path: number[];
  /** every section on a page with content that the walk jumps over */
  skippedSections: Set<string>;
}

/**
 * The route through the pages, given the answers so far.
 *
 * From the first page with content, each step is the page a firing `goto`
 * names, or else the next page with content. A goto is read only from a page
 * at or after the last page that holds one of its conditions — a rule cannot
 * skip past the question it is about — and only forward: a target on or
 * before the current page is ignored, so a rule can never loop. A target that
 * has no content right now lands on the first page after it that has.
 *
 * Deriving the route from the answers (rather than remembering where the
 * respondent has been) is what lets Back retrace a jump after a draft is
 * resumed, and what makes the pages jumped over a fact the submit path can
 * ask for: they are the pages with content that are not on the route.
 */
export function walkPages(pages: FormPage[], def: PublishedDef, ev: FormEval, cfg: FormConfig): PageWalk {
  const visible = pages.map((_, i) => i).filter((i) => pageHasContent(pages[i], def, ev, cfg));
  const skippedSections = new Set<string>();
  if (!visible.length) return { path: [], skippedSections };

  const keysByPage = pages.map((p) => pageFieldKeys(p, def));
  const pageOf = (key: string) => {
    const head = key.includes('.') ? key.slice(0, key.indexOf('.')) : key;
    return keysByPage.findIndex((ks) => ks.has(head));
  };
  const gotos = (def.rules || []).flatMap((rule) => (rule.actions || [])
    .filter((a): a is Extract<typeof a, { type: 'goto' }> => a.type === 'goto')
    .map((a) => ({
      rule,
      target: pageIndexOf(pages, a.page),
      anchor: rule.trigger === 'always' ? 0 : Math.max(0, ...(rule.when || []).map((c) => pageOf(c.field))),
    })));

  const path: number[] = [];
  let at: number | undefined = visible[0];
  while (at !== undefined && !path.includes(at)) {
    path.push(at);
    const here: number = at;
    const jump: (typeof gotos)[number] | undefined = gotos.find((g) => g.target > here && g.anchor <= here && ruleFires(g.rule, ev.order.values));
    const after = visible.filter((v) => v > here);
    at = jump ? after.find((v) => v >= jump.target) : after[0];
  }
  for (const v of visible) {
    if (!path.includes(v)) pages[v].sections.forEach((s) => skippedSections.add(s.id));
  }
  return { path, skippedSections };
}
